import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import { GetCompanyById, UpdateCompany } from "../../../api/utility/companyAPI";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";
import { IMAGE_URL } from "../../../utils/api-config";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Company name is required"),
  address: yup.string().required("Address is required"),
  email: yup.string().email("Invalid email").nullable(),
  phone: yup.string().nullable(),
  mobile: yup
    .string()
    .required("Mobile is required")
    .matches(/^[0-9]{10}$/, "Must be a 10-digit number"),
  website: yup.string().url("Invalid website URL").nullable()
});

const EditCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id } = useParams();

  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const handleFileChange = event => {
    const file = event.target.files[0]; // Extract the first file
    if (file) {
      setLogoPreview(URL.createObjectURL(file)); // Set preview
      setLogo(file);
    }
  };

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {}
  });

  const fetchData = async () => {
    try {
      const response = await GetCompanyById(id); // Make sure login function returns token
      if (response.success) {
        Object.entries(response.data).forEach(([key, value]) => {
          setValue(key, value);
        });
        if (response.data.logo) {
          setLogoPreview(`${IMAGE_URL}${response.data.logo}`);
        }
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(
    () => {
      if (!id) return;
      // Fetch existing data
      const loadAllData = async () => {
        try {
          // ✅ CALL AFTER ALL ABOVE ARE DONE
          fetchData();
        } catch (error) {
          console.error("Error loading master data:", error);
        }
      };
      loadAllData();
    },
    [id]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const formData = new FormData();

      for (const key in data) {
        if (data[key] !== "logo") {
          formData.append(key, data[key]);
        }
      }
      if (logo) {
        formData.append("logo", logo);
      }

      const response = await UpdateCompany(id, formData);
      if (response.success) {
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Company" title="Edit Company" />
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex justify-content-between">
            <h5>Edit Company</h5>
            <Link to={`/utility/company/list`}>
              <button className="btn btn-primary" id="list">
                List Companies
              </button>
              <UncontrolledTooltip target="list">
                List Companies
              </UncontrolledTooltip>
            </Link>
          </div>

          <form
            ref={formRef}
            className="form theme-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="card-body row">
              {/* Company Name */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Company Name</label>
                  <input className="form-control" {...register("name")} />
                  {errors.name &&
                    <p className="text-danger">
                      {errors.name.message}
                    </p>}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label>Company Description</label>
                  <textarea
                    className="form-control"
                    {...register("description")}
                  />
                  {errors.description &&
                    <p className="text-danger">
                      {errors.description.message}
                    </p>}
                </div>
              </div>

              {/* Address */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Address</label>
                  <input className="form-control" {...register("address")} />
                  {errors.address &&
                    <p className="text-danger">
                      {errors.address.message}
                    </p>}
                </div>
              </div>

              {/* Email */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-control" {...register("email")} />
                  {errors.email &&
                    <p className="text-danger">
                      {errors.email.message}
                    </p>}
                </div>
              </div>

              {/* Phone */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Phone</label>
                  <input className="form-control" {...register("phone")} />
                  {errors.phone &&
                    <p className="text-danger">
                      {errors.phone.message}
                    </p>}
                </div>
              </div>

              {/* Mobile */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Mobile</label>
                  <input className="form-control" {...register("mobile")} />
                  {errors.mobile &&
                    <p className="text-danger">
                      {errors.mobile.message}
                    </p>}
                </div>
              </div>

              {/* Website */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Website</label>
                  <input className="form-control" {...register("website")} />
                  {errors.website &&
                    <p className="text-danger">
                      {errors.website.message}
                    </p>}
                </div>
              </div>
              {/* Website */}
              <div className="col-md-4">
                <div className="form-group">
                  <label>Logo</label>
                  <input
                    type="file"
                    className="form-control"
                    {...register("logo")}
                    onChange={handleFileChange}
                  />
                  {errors.logo &&
                    <p className="text-danger">
                      {errors.logo.message}
                    </p>}
                </div>
                {logoPreview &&
                  <img
                    src={logoPreview}
                    alt="Preview"
                    width={100}
                    height={100}
                  />}
              </div>
            </div>

            <div className="card-footer text-end">
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditCompany;
